/**
 * OcrFigurePickModal — **서버가 이미 가진 그 문제의 그림**을 골라 본문에 넣는다.
 *
 * ── 왜 필요한가(오너 요청 2026-09-04) ──
 * 검수 중에 본문에서 그림을 지웠다가 되돌리고 싶을 때, 지금은 길이 없었다. 실행 취소로 못 돌아온
 * 자리(탭을 옮겼거나 문제를 다시 읽은 뒤)에서는 **서버에 그림이 멀쩡히 있는데 본문에만 없는**
 * 상태가 되고, 그러면 편집본과 원문의 그림 수가 어긋나 그 field 의 그림 편집이 잠긴다.
 *
 * ── 왜 등록이 아니라 「넣기」인가 ──
 * 여기서 고르는 그림은 **서버가 이미 세고 있는 것**이다(계약 §10 · 회신 §5 의 payload 기준 목록).
 * 그러니 등록(`POST …/figures`)을 부르면 같은 그림이 둘이 된다. 편집본에 노드만 넣으면 개수가
 * 맞아떨어지고, 그때부터 그 그림의 편집·AI 요청이 다시 열린다.
 *
 * 그래서 이 창은 **서버를 쓰지 않는다** — 목록을 읽기만 하고(GET), 넣는 일은 편집기가 한다.
 * 저장은 사람이 [저장]을 누를 때 평소대로 나간다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button } from '../common';
import { fetchFigures, AUTH_REDIRECT } from '../FigureEditor/figureApi';
import { describeFigureError } from '../FigureEditor/figureText';
import { collectFigureNodes } from '../FigureEditor/figureMatch';

/** 눈으로 고르게 하는 썸네일. 지금 그려진 것(svg·src)이 먼저이고, 없으면 페이지 크롭(계약 §3-1). */
function thumbOf(item) {
  if (typeof item?.svg === 'string' && item.svg) return item.svg;
  if (typeof item?.src === 'string' && item.src) return item.src;
  if (typeof item?.crop === 'string' && item.crop) return item.crop;
  return null;
}

export default function OcrFigurePickModal({
  isOpen, problemId, field, docState, getAccessToken, onClose, onPick,
}) {
  const where = field === 'solution' ? '풀이' : '본문';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  /** 지금 본문에 이미 있는 그림들 — 「또 넣는 것」을 화면이 먼저 알려 주기 위한 재료다. */
  const [inDoc, setInDoc] = useState(() => new Set());

  const seqRef = useRef(0);
  const load = useCallback(async () => {
    const seq = (seqRef.current += 1);
    setLoading(true);
    setError(null);
    try {
      const d = await fetchFigures('ocr', problemId, getAccessToken);
      if (d === AUTH_REDIRECT || seq !== seqRef.current) return;
      setItems((Array.isArray(d?.items) ? d.items : []).filter((it) => it.field === field));
    } catch (e) {
      if (seq !== seqRef.current) return;
      setError(describeFigureError(e).text);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [problemId, field, getAccessToken]);

  useEffect(() => {
    if (!isOpen) return;
    const nodes = collectFigureNodes(docState);
    setInDoc(new Set((nodes || []).map((n) => n.src).filter((v) => typeof v === 'string' && v)));
    load();
  }, [isOpen, docState, load]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${where}에 저장된 그림 넣기`}
      size="md"
      zIndex={9999986}
    >
      <div className="space-y-3">
        <p className="text-xs leading-relaxed text-gray-600">
          서버가 이 문제의 {where}에 가진 그림입니다. 고르면 {where} 끝에 넣습니다 — [저장]해야 반영됩니다.
        </p>

        {loading && <div className="h-24 rounded-xl bg-gray-100 motion-safe:animate-pulse" />}

        {!loading && error && (
          <div className="space-y-2">
            <p role="alert" className="text-xs text-danger">{error}</p>
            <Button size="xs" variant="secondary" onClick={load}>다시 시도</Button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <p className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
            서버가 가진 {where} 그림이 없습니다 — 넣을 것이 없습니다.
            새로 만들려면 [AI 생성]이나 [이미지 파일 추가]를 쓰세요.
          </p>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {items.map((it) => {
              const thumb = thumbOf(it);
              // 이미 본문에 있는 그림 — 막지는 않는다(같은 그림을 둘 두는 것도 사람의 선택이다).
              // 다만 그러면 개수가 맞지 않아 그림 편집이 잠기므로, 고르기 전에 말해 준다.
              const already = typeof it.src === 'string' && inDoc.has(it.src);
              return (
                <button
                  key={it.index}
                  type="button"
                  onClick={() => onPick?.({ src: it.src, index: it.index })}
                  disabled={typeof it.src !== 'string' || !it.src}
                  className="rounded-xl border border-gray-200 bg-white p-1.5 text-left hover:border-primary disabled:opacity-50"
                >
                  {thumb
                    ? <img src={thumb} alt="" className="h-16 w-full rounded bg-white object-contain" />
                    : <span className="flex h-16 w-full items-center justify-center rounded bg-gray-50 text-[10px] text-gray-400">받지 못함</span>}
                  <span className="mt-1 block text-[11px] font-medium text-gray-800">{where} {it.index + 1}번째</span>
                  <span className="block text-[10px] text-gray-500">
                    {it.kind === 'svg' ? '선 그림' : '사진'}
                    {already && <b className="text-amber-700"> · 이미 본문에 있음</b>}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex justify-end">
          <Button size="sm" variant="secondary" onClick={onClose}>닫기</Button>
        </div>
      </div>
    </Modal>
  );
}
